import React from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
`;

const ErrorCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  padding: 3rem;
  text-align: center;
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
`;

const ErrorIcon = styled.svg`
  width: 40px;
  height: 40px;
  fill: white;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 1rem;
`;

const Description = styled.p`
  font-size: 1.125rem;
  color: #718096;
  line-height: 1.6;
  margin: 0 0 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f7fafc;
    color: #4a5568;
    border: 2px solid #e2e8f0;
    
    &:hover {
      background: #edf2f7;
      border-color: #cbd5e0;
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

const ErrorDetails = styled.details`
  margin-top: 2rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  text-align: left;
  
  summary {
    cursor: pointer;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 0.5rem;
    
    &:hover {
      color: #2d3748;
    }
  }
  
  pre {
    margin-top: 0.5rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: monospace;
    font-size: 0.875rem;
    color: #718096;
  }
`;

const EventId = styled.div`
  margin-top: 2rem;
  padding: 0.75rem;
  background: #edf2f7;
  border-radius: 8px;
  font-family: monospace;
  font-size: 0.875rem;
  color: #718096;
`;

interface SentryErrorFallbackProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError: () => void;
}

export const SentryErrorFallback: React.FC<SentryErrorFallbackProps> = ({
  error,
  componentStack,
  eventId,
  resetError,
}) => {
  const isDevelopment = import.meta.env.DEV;
  const { t } = useTranslation();

  const handleReportFeedback = () => {
    if (eventId) {
      Sentry.showReportDialog({ eventId });
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    resetError();
    // Use window.location instead of useNavigate to avoid Router context issues
    window.location.href = '/dashboard';
  };

  return (
    <ErrorContainer>
      <ErrorCard>
        <IconContainer>
          <ErrorIcon viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </ErrorIcon>
        </IconContainer>

        <Title>{t('errors.somethingWentWrong')}</Title>
        <Description>
          {t('errors.sorryMessage')}
        </Description>

        <ButtonGroup>
          {eventId && (
            <Button variant="secondary" onClick={handleReportFeedback}>
              {t('errors.reportFeedback')}
            </Button>
          )}
          <Button variant="secondary" onClick={handleReload}>
            {t('errors.reloadPage')}
          </Button>
          <Button variant="primary" onClick={handleGoHome}>
            {t('errors.goToDashboard')}
          </Button>
        </ButtonGroup>

        {eventId && (
          <EventId>
            {t('errors.errorId')} {eventId}
          </EventId>
        )}

        {isDevelopment && (
          <ErrorDetails>
            <summary>{t('errors.devErrorDetails')}</summary>
            <pre>
              {error.toString()}
              {componentStack && `\n\nComponent Stack:${componentStack}`}
            </pre>
          </ErrorDetails>
        )}
      </ErrorCard>
    </ErrorContainer>
  );
};
